import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { authApi } from '../../api/authApi';
import { useAppState } from '../../reduxish/appState';

interface Props {
    open: boolean;
    /**
     * True when the account has a local password (i.e. not OAuth-only). When
     * true the user must re-enter their password to confirm deletion.
     */
    requiresPassword: boolean;
    onClose: () => void;
}

const CONFIRM_PHRASE = 'DELETE';

export default function DeleteAccountDialog({ open, requiresPassword, onClose }: Props) {
    const { state } = useAppState();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleClose = () => {
        if (submitting) return;
        setPassword('');
        setConfirmText('');
        setError('');
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (confirmText !== CONFIRM_PHRASE) {
            setError(`Please type ${CONFIRM_PHRASE} to confirm.`);
            return;
        }
        if (requiresPassword && !password) {
            setError('Please enter your current password.');
            return;
        }

        setSubmitting(true);
        try {
            await authApi.deleteAccount(requiresPassword ? password : undefined);
            // Clear local session and redirect home.
            await state.auth?.signOut();
            navigate('/', { replace: true });
        } catch (err: any) {
            setError(err?.message || 'Could not delete your account.');
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <form onSubmit={handleSubmit}>
                <DialogTitle>Delete account</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        This permanently deactivates your account and removes your profile
                        information. Your contribution history is anonymized and cannot be
                        recovered.
                    </DialogContentText>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Stack spacing={2}>
                        {requiresPassword && (
                            <TextField
                                type="password"
                                label="Current password"
                                fullWidth
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                disabled={submitting}
                                autoComplete="current-password"
                            />
                        )}
                        <TextField
                            label={`Type ${CONFIRM_PHRASE} to confirm`}
                            fullWidth
                            value={confirmText}
                            onChange={e => setConfirmText(e.target.value)}
                            disabled={submitting}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                    <Button
                        type="submit"
                        color="error"
                        variant="contained"
                        disabled={submitting || confirmText !== CONFIRM_PHRASE}
                    >
                        {submitting ? 'Deleting…' : 'Delete account'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
